import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  fetchParentConsentState,
  fetchSampleQuestion,
  fetchSubjects,
  resendParentConsent,
  type ConsentChannel,
} from "../../api/onboarding";
import { ApiError } from "../../api/client";
import { useAsync } from "../../lib/useAsync";
import { WaitingRoomScreen, type WaitingSample } from "../../screens/onboarding/WaitingRoomScreen";

/** The API returns options as a map or a list depending on the question. */
function toOptions(
  options: { id: string; text: string }[] | Record<string, string> | null,
): { id: string; text: string }[] {
  if (Array.isArray(options)) return options;
  if (!options) return [];
  return Object.entries(options).map(([id, text]) => ({ id, text: String(text) }));
}

/**
 * The consent waiting room, on real data and with working controls.
 *
 * Three things here did nothing. The sample question was four list items under
 * the word "try one", so tapping an answer had no effect. "Send it again" only
 * reset its own countdown and never asked for anything. And the question
 * itself was hardcoded algebra — the first thing a student ever sees from us,
 * invented.
 *
 * The sample is fetched but never submitted: a pre-consent account must
 * generate no activity (HR-3), so this opens no session and records nothing.
 */
export function WaitingRoomRoute() {
  const navigate = useNavigate();
  const state = (useLocation().state ?? {}) as {
    sentTo?: string;
    channel?: ConsentChannel;
    canResendAt?: string;
  };

  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  // Moves forward on every accepted send, so the countdown tracks the server
  // rather than restarting itself.
  const [canResendAt, setCanResendAt] = useState<string | undefined>(state.canResendAt);

  const sample = useAsync(async (): Promise<WaitingSample | null> => {
    const catalog = await fetchSubjects();
    const subject = catalog.subjects[Math.floor(Math.random() * catalog.subjects.length)];
    if (!subject) return null;

    const set = await fetchSampleQuestion(subject.id);
    const question = set.questions[0];
    if (!question) return null;

    const options = toOptions(question.options);
    // A question with nothing to choose between is worse than no sample.
    if (options.length < 2) return null;

    return {
      id: question.id,
      subject: question.subject?.name ?? subject.name,
      stem: question.question_text,
      options,
      correctOptionId: question.correct_answer ?? "",
      explanation: question.explanation ?? "",
    };
  });

  /*
   * Watch for the approval this screen promises.
   *
   * "This screen updates the moment they approve" was copy with nothing behind
   * it: a parent could approve in seconds and the student would sit here
   * forever, then tap resend and be told there was no pending request — true,
   * and unhelpful, because it had already been approved.
   *
   * Polled rather than pushed. A parent taps a link in their own browser and
   * there is no socket back to the student's phone; four seconds is fast
   * enough to feel immediate and slow enough not to matter on a data plan.
   */
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    let active = true;

    const check = async () => {
      try {
        const state = await fetchParentConsentState();
        if (!active) return;

        if (state.approved) {
          // Straight on to the exam picker, the next step they were held at.
          navigateRef.current("/exam", { replace: true });
          return;
        }

        // Also the refresh case: the screen loses its navigation state, and
        // this is where the cooldown and the contact come back from.
        if (state.canResendAt) setCanResendAt(state.canResendAt);
        if (state.status === "rejected" || state.status === "expired") {
          setMessage(
            state.status === "rejected"
              ? "Your parent said no. You can ask someone else instead."
              : "That link expired. Send a new one.",
          );
        }
      } catch {
        // A failed poll is not worth telling the student about; the next one
        // is four seconds away.
      }
    };

    void check();
    const timer = setInterval(() => void check(), 4000);

    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  const resend = async () => {
    if (resending) return;
    setResending(true);
    setMessage(null);

    try {
      const result = await resendParentConsent();
      setCanResendAt(result.canResendAt);

      // Sent by the server on every channel: the link never reaches the
      // student's device, so they cannot approve their own consent.
      setMessage(`Sent again to ${result.sentTo}.`);
    } catch (error) {
      setMessage(
        error instanceof ApiError ? error.message : "We couldn't send that again just now.",
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <WaitingRoomScreen
      sentTo={state.sentTo ?? ""}
      channel={state.channel ?? "sms"}
      {...(canResendAt ? { canResendAt } : {})}
      // Null while it loads, and null if it fails — the waiting room is about
      // the consent request, and a sample that will not load must not take the
      // screen down with it.
      sample={sample.data ?? null}
      onResend={() => void resend()}
      resending={resending}
      resendMessage={message}
      onChangeContact={() => void navigate("/parent-phone")}
    />
  );
}
